import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Chess } from 'chess.js';
import { api } from '@/lib/api';
import ChessBoardView from '@/components/chess/ChessBoardView';
import { Flame, ChevronLeft, ChevronRight } from 'lucide-react';

const FILES = 'abcdefgh';
const INITIAL_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

function normalizeStep(step, fallbackFen) {
  return {
    title: step?.title || 'Posición inicial',
    explanation: step?.explanation || '',
    fen: step?.fen || fallbackFen || INITIAL_FEN,
    ignitedLines: Array.isArray(step?.ignitedLines) ? step.ignitedLines : [],
    arrows: Array.isArray(step?.arrows) ? step.arrows : [],
    circles: step?.circles && typeof step.circles === 'object' ? step.circles : {},
  };
}

export default function ClassroomLavaPresentation({
  activeExplanation,
  roomId,
  isTeacher = false,
}) {
  const activity = activeExplanation?.activity;
  const config = activity?.lava_config || {};
  const steps = useMemo(() => {
    const saved = Array.isArray(config.steps) && config.steps.length
      ? config.steps
      : [{
          title: 'Posición inicial',
          explanation: '',
          fen: activity?.fen || INITIAL_FEN,
          ignitedLines: config.ignitedLines || [],
          arrows: [],
          circles: {},
        }];
    return saved.map(step => normalizeStep(step, activity?.fen));
  }, [config, activity]);

  const currentStep = Math.max(
    0,
    Math.min(Number(activeExplanation?.current_step) || 0, steps.length - 1)
  );
  const savedStep = steps[currentStep] || steps[0];
  const serverState = activeExplanation?.lava_state;
  const matchingServerState =
    serverState && Number(serverState.current_step) === currentStep
      ? serverState
      : null;

  const [fen, setFen] = useState(savedStep.fen);
  const [ignitedLines, setIgnitedLines] = useState(savedStep.ignitedLines);
  const [arrows, setArrows] = useState(savedStep.arrows);
  const [circles, setCircles] = useState(savedStep.circles);
  const [boardWidth, setBoardWidth] = useState(320);
  const boardContainerRef = useRef(null);
  const loadedStepRef = useRef('');
  const lastServerStateRef = useRef(null);

  const loadScene = useCallback((scene, liveState = null) => {
    const source = liveState || scene;
    setFen(source.fen || scene.fen || INITIAL_FEN);
    setIgnitedLines(Array.isArray(source.ignited_lines)
      ? source.ignited_lines
      : Array.isArray(source.ignitedLines)
        ? source.ignitedLines
        : scene.ignitedLines);
    setArrows(Array.isArray(source.arrows) ? source.arrows : scene.arrows);
    setCircles(source.circles && typeof source.circles === 'object'
      ? source.circles
      : scene.circles);
  }, []);

  useEffect(() => {
    const key = `${activity?.id || ''}:${currentStep}`;
    if (loadedStepRef.current === key) return;
    loadedStepRef.current = key;
    loadScene(savedStep, matchingServerState);
  }, [activity?.id, currentStep, savedStep, matchingServerState, loadScene]);

  // Los alumnos aplican el estado publicado por el profesor al recibir el polling.
  useEffect(() => {
    if (isTeacher || !matchingServerState) return;
    if (lastServerStateRef.current === matchingServerState) return;
    lastServerStateRef.current = matchingServerState;
    loadScene(savedStep, matchingServerState);
  }, [isTeacher, matchingServerState, savedStep, loadScene]);

  useEffect(() => {
    const node = boardContainerRef.current;
    if (!node) return;
    const measure = () => {
      const width = node.clientWidth || 320;
      const availableHeight = window.innerHeight - 200;
      const maxBoardByHeight = Math.max(200, availableHeight);
      setBoardWidth(Math.max(200, Math.min(600, width, maxBoardByHeight)));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const publishState = useCallback(async (next) => {
    if (!isTeacher || !roomId) return;
    try {
      await api.post(`/classrooms/${roomId}/explanation/lava-state`, {
        current_step: currentStep,
        fen: next.fen,
        ignited_lines: next.ignitedLines,
        arrows: next.arrows,
        circles: next.circles,
        title: savedStep.title,
        explanation: savedStep.explanation,
      });
    } catch (error) {
      console.error('No se pudo sincronizar Lava Floor:', error);
    }
  }, [isTeacher, roomId, currentStep, savedStep]);

  const handlePieceDrop = useCallback((from, to) => {
    if (!isTeacher) return false;
    try {
      const game = new Chess(fen);
      const move = game.move({ from, to, promotion: 'q' });
      if (!move) return false;
      const nextFen = game.fen();
      setFen(nextFen);
      void publishState({
        fen: nextFen,
        ignitedLines,
        arrows,
        circles,
      });
      return true;
    } catch {
      return false;
    }
  }, [isTeacher, fen, publishState, ignitedLines, arrows, circles]);

  const toggleLine = useCallback((type, value) => {
    if (!isTeacher) return;
    const key = `${type}:${value}`;
    const nextLines = ignitedLines.includes(key)
      ? ignitedLines.filter(item => item !== key)
      : [...ignitedLines, key];
    setIgnitedLines(nextLines);
    void publishState({ fen, ignitedLines: nextLines, arrows, circles });
  }, [isTeacher, ignitedLines, fen, arrows, circles, publishState]);

  const handleAnnotationsChange = useCallback((nextArrows, nextCircles) => {
    if (!isTeacher) return;
    const safeArrows = Array.isArray(nextArrows) ? nextArrows : [];
    const safeCircles = nextCircles && typeof nextCircles === 'object' ? nextCircles : {};
    setArrows(safeArrows);
    setCircles(safeCircles);
    void publishState({
      fen,
      ignitedLines,
      arrows: safeArrows,
      circles: safeCircles,
    });
  }, [isTeacher, fen, ignitedLines, publishState]);

  const extraSquareStyles = useMemo(() => {
    const styles = {};
    ignitedLines.forEach(key => {
      const [type, value] = String(key).split(':');
      if (type === 'rank' && /^[1-8]$/.test(value)) {
        for (const file of FILES) {
          styles[`${file}${value}`] = {
            backgroundColor: '#21120d',
            backgroundImage: "url('/images/chess4kings-lava-texture.png')",
            backgroundSize: '520px 65px',
            backgroundPosition: `${-FILES.indexOf(file) * 65}px 0px`,
            backgroundRepeat: 'no-repeat',
            boxShadow: 'inset 0 0 0 2px rgba(249,115,22,.85)',
          };
        }
      } else if (type === 'file' && /^[a-h]$/.test(value)) {
        for (let rank = 1; rank <= 8; rank++) {
          styles[`${value}${rank}`] = {
            backgroundColor: '#21120d',
            backgroundImage: "url('/images/chess4kings-lava-texture-vertical.png')",
            backgroundSize: '65px 520px',
            backgroundPosition: `0px ${-(rank - 1) * 65}px`,
            backgroundRepeat: 'no-repeat',
            boxShadow: 'inset 0 0 0 2px rgba(249,115,22,.85)',
          };
        }
      }
    });
    return styles;
  }, [ignitedLines]);

  // Detecta jaque real y comprueba si la lava cruza la casilla del rey.
  const lavaStatus = useMemo(() => {
    try {
      const game = new Chess(fen);
      if (!game.isCheck()) return null;

      const kingColor = game.turn();
      let kingSquare = null;

      game.board().forEach((row, rowIndex) => {
        row.forEach((piece, fileIndex) => {
          if (piece && piece.type === 'k' && piece.color === kingColor) {
            kingSquare = `${FILES[fileIndex]}${8 - rowIndex}`;
          }
        });
      });

      if (!kingSquare) return null;

      const lavaTouchesKing =
        ignitedLines.includes(`rank:${kingSquare[1]}`) ||
        ignitedLines.includes(`file:${kingSquare[0]}`);

      if (!lavaTouchesKing) return null;

      return {
        type: game.isCheckmate() ? 'checkmate' : 'warning',
        kingSquare,
      };
    } catch {
      return null;
    }
  }, [fen, ignitedLines]);

  const lavaType = lavaStatus?.type || '';
  const lavaKingSquare = lavaStatus?.kingSquare || '';
  const [lavaEffect, setLavaEffect] = useState(null);
  const lastLavaEffectKeyRef = useRef('');

  // La advertencia persiste mientras el rey siga bajo amenaza de lava.
  // El jaque mate inicia una secuencia de tres destellos y después la celebración.
  useEffect(() => {
    if (!lavaType || !lavaKingSquare) {
      lastLavaEffectKeyRef.current = '';
      setLavaEffect(null);
      return undefined;
    }

    const key =
      `${activity?.id || ''}:${currentStep}:${fen}:${lavaType}:${lavaKingSquare}`;

    if (lavaType === 'warning') {
      lastLavaEffectKeyRef.current = '';
      setLavaEffect({
        type: 'warning',
        phase: 'warning',
        kingSquare: lavaKingSquare,
        visible: true,
      });
      return undefined;
    }

    if (lastLavaEffectKeyRef.current === key) return undefined;

    lastLavaEffectKeyRef.current = key;

    const timers = [];
    let cancelled = false;

    const schedule = (callback, delay) => {
      timers.push(window.setTimeout(() => {
        if (!cancelled) callback();
      }, delay));
    };

    setLavaEffect({
      type: 'checkmate',
      phase: 'checkmate',
      kingSquare: lavaKingSquare,
      visible: true,
    });

    // Tres destellos de CHECKMATE.
    for (let i = 1; i <= 6; i++) {
      schedule(() => {
        setLavaEffect(prev => prev ? { ...prev, visible: i % 2 === 0 } : prev);
      }, i * 350);
    }

    // Después, tres destellos de la celebración del rey atrapado.
    schedule(() => {
      setLavaEffect(prev => prev ? {
        ...prev,
        phase: 'trapped',
        visible: true,
      } : prev);
    }, 2450);

    for (let i = 1; i <= 6; i++) {
      schedule(() => {
        setLavaEffect(prev => prev ? { ...prev, visible: i % 2 === 0 } : prev);
      }, 2450 + i * 350);
    }

    // Deja el mensaje final visible para que todos puedan leerlo.
    schedule(() => {
      setLavaEffect(prev => prev ? {
        ...prev,
        phase: 'trapped',
        visible: true,
      } : prev);
    }, 5000);

    schedule(() => setLavaEffect(null), 9500);

    return () => {
      cancelled = true;
      timers.forEach(window.clearTimeout);
    };
  }, [activity?.id, currentStep, fen, lavaType, lavaKingSquare]);

  return (
    <div
      data-testid="classroom-lava-presentation"
      className="fixed inset-0 z-[55] overflow-y-auto bg-slate-950/95 p-3 pb-24 text-white sm:p-5 sm:pb-24"
    >
      <div className="mx-auto max-w-6xl">
        <header className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="flex items-center gap-2 text-orange-300">
              <Flame className="h-5 w-5" />
              <span className="text-xs font-bold uppercase tracking-widest">
                Chess4Kings · El piso es lava
              </span>
            </div>
            <h2 className="mt-1 text-xl font-bold sm:text-2xl">
              {savedStep.title}
            </h2>
          </div>
          <span className="rounded-full border border-orange-400/40 px-3 py-1 text-xs text-orange-200">
            Escena {currentStep + 1} de {steps.length}
          </span>
        </header>

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
          <section className="min-w-0 rounded-xl border border-slate-700 bg-slate-900 p-2 sm:p-4">
            <div ref={boardContainerRef} className="relative mx-auto w-full max-w-[480px]">
              <ChessBoardView
                fen={fen}
                onPieceDrop={handlePieceDrop}
                boardWidth={boardWidth}
                arePiecesDraggable={isTeacher}
                allowAnnotations={isTeacher}
                clearAnnotationsOnLeftClick={isTeacher}
                arrows={arrows}
                circles={circles}
                onAnnotationsChange={handleAnnotationsChange}
                extraSquareStyles={extraSquareStyles}
              />
              {lavaEffect && (
                <div
                  className="pointer-events-none absolute left-1/2 top-0 z-20"
                  style={{
                    width: boardWidth,
                    height: boardWidth,
                    transform: 'translateX(-50%)',
                  }}
                  aria-live="polite"
                >
                  <div
                    className={`absolute -translate-x-1/2 -translate-y-1/2 text-2xl sm:text-3xl ${
                      lavaEffect.type === 'checkmate'
                        ? 'animate-bounce'
                        : 'animate-pulse'
                    }`}
                    style={{
                      left: `${((FILES.indexOf(lavaEffect.kingSquare[0]) + 0.5) / 8) * 100}%`,
                      top: `${((8 - Number(lavaEffect.kingSquare[1]) + 0.5) / 8) * 100}%`,
                    }}
                  >
                    🔥🔥
                  </div>

                  {lavaEffect.type === 'checkmate' ? (
                    <>
                      {Array.from({ length: 36 }, (_, index) => (
                        <span
                          key={`lava-confetti-${index}`}
                          className={`absolute text-xl sm:text-2xl ${
                            lavaEffect.visible ? 'animate-bounce opacity-100' : 'opacity-20'
                          }`}
                          style={{
                            left: `${4 + ((index * 29) % 92)}%`,
                            top: `${4 + ((index * 17) % 88)}%`,
                            animationDelay: `${(index % 8) * 70}ms`,
                          }}
                        >
                          {['🎉', '✨', '🔥', '⭐', '👑', '🟠'][index % 6]}
                        </span>
                      ))}
                      <div className="absolute inset-0 flex items-center justify-center bg-slate-950/45 px-2">
                        {lavaEffect.phase === 'trapped' ? (
                          <span
                            className={`rounded-2xl border-4 border-orange-400 bg-slate-950/95 px-4 py-5 text-center text-3xl font-black leading-tight text-orange-300 shadow-[0_0_45px_rgba(249,115,22,0.9)] sm:px-7 sm:py-7 sm:text-6xl ${
                              lavaEffect.visible ? 'scale-100 opacity-100' : 'scale-90 opacity-20'
                            } transition-all duration-150`}
                          >
                            🔥 ¡EL REY FUE CAPTURADO! 🔥
                          </span>
                        ) : (
                          <span
                            className={`rounded-xl border-4 border-yellow-300 bg-slate-950/95 px-4 py-4 text-center text-4xl font-black tracking-wider text-yellow-300 shadow-[0_0_40px_rgba(250,204,21,0.9)] sm:px-8 sm:py-6 sm:text-7xl ${
                              lavaEffect.visible ? 'scale-100 opacity-100' : 'scale-90 opacity-20'
                            } transition-all duration-150`}
                          >
                            CHECKMATE
                          </span>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center px-2">
                      <span className="animate-pulse rounded-xl border-2 border-orange-400 bg-slate-950/90 px-3 py-3 text-center text-xl font-black text-orange-300 shadow-2xl sm:px-5 sm:text-3xl">
                        🔥 ¡EL PISO ES LAVA! 🔥
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>

          <aside className="space-y-4">
            <section className="rounded-xl border border-slate-700 bg-slate-900 p-4">
              <h3 className="mb-2 font-semibold text-orange-200">Misión</h3>
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-200">
                {savedStep.explanation || 'Observa la posición y descubre cómo funciona el piso es lava.'}
              </p>
              <p className="mt-3 text-xs text-slate-400">
                {isTeacher
                  ? 'Puedes mover piezas legalmente, encender filas o columnas y marcar jugadas.'
                  : 'Observa los movimientos, las líneas de lava y las anotaciones del profesor.'}
              </p>
            </section>

            {isTeacher && (
            <section className="rounded-xl border border-orange-400/30 bg-slate-900 p-3">
              <h3 className="mb-3 flex items-center gap-2 font-semibold text-orange-200">
                <Flame className="h-4 w-4" /> Control de lava
              </h3>
              <p className="mb-2 text-xs font-semibold text-slate-300">Filas</p>
              <div className="grid grid-cols-4 gap-1">
                {'12345678'.split('').map(value => {
                  const active = ignitedLines.includes(`rank:${value}`);
                  return (
                    <button
                      key={`rank-${value}`}
                      type="button"
                      disabled={!isTeacher}
                      aria-pressed={active}
                      onClick={() => toggleLine('rank', value)}
                      className={`rounded border px-1 py-2 text-xs ${
                        active ? 'border-orange-400 bg-orange-500/20 text-orange-200' : 'border-slate-600 text-slate-200'
                      } disabled:cursor-default`}
                    >
                      {active ? '🔥 ' : ''}{value}
                    </button>
                  );
                })}
              </div>
              <p className="mb-2 mt-4 text-xs font-semibold text-slate-300">Columnas</p>
              <div className="grid grid-cols-4 gap-1">
                {FILES.split('').map(value => {
                  const active = ignitedLines.includes(`file:${value}`);
                  return (
                    <button
                      key={`file-${value}`}
                      type="button"
                      disabled={!isTeacher}
                      aria-pressed={active}
                      onClick={() => toggleLine('file', value)}
                      className={`rounded border px-1 py-2 text-xs ${
                        active ? 'border-orange-400 bg-orange-500/20 text-orange-200' : 'border-slate-600 text-slate-200'
                      } disabled:cursor-default`}
                    >
                      {active ? '🔥 ' : ''}{value.toUpperCase()}
                    </button>
                  );
                })}
              </div>
              <p className="mt-3 text-xs text-slate-400">
                {ignitedLines.length} línea(s) encendida(s)
              </p>
            </section>
            )}

            {!isTeacher && (
              <div className="rounded-lg border border-slate-700 bg-slate-900 p-3 text-xs text-slate-400">
                Solo el profesor puede mover piezas, cambiar la lava y agregar anotaciones.
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}
